import { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LanguageIcon from '@mui/icons-material/Language';
import TokenIcon from '@mui/icons-material/Token';

import { AuthStoreContext } from '../../Root';
import AdminNodeStore from '../../../stores/admin-node-store';
import { Token, TokenService } from '../../../http/token-service';
import { DbNode, NodeType } from '../../../types/node';
import NodeView from '../../dashboard/node/NodeView';
import { NodeChildrenSkeleton } from '../../dashboard/node/NodeItem';
import TokenDocsDialog from './TokenDocsDialog';

interface TokenRowProps {
    token: Token;
    depth: number;
    onRemoved: () => void;
}

function TokenRow({ token, depth, onRemoved }: TokenRowProps) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleClose = () => {
        if (loading) return;
        setError('');
        setOpen(false);
    };

    const handleRemove = async () => {
        setLoading(true);
        setError('');
        try {
            await TokenService.removeToken(token.id);
            onRemoved();
            setOpen(false);
        } catch {
            setError('Не удалось удалить токен');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <ListItem
                secondaryAction={
                    <Tooltip title="Удалить токен">
                        <IconButton edge="end" onClick={() => setOpen(true)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                }
                sx={{ pl: 1 + depth * 2, pr: 6 }}
            >
                <TokenIcon fontSize="small" color="action" sx={{ mr: 1.5 }} />
                <ListItemText primary={token.description} slotProps={{ primary: { variant: 'body2' } }} />
            </ListItem>
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Удалить токен</DialogTitle>
                <DialogContent>
                    <DialogContentText>Токен «{token.description}» перестанет работать. Продолжить?</DialogContentText>
                    {error && (
                        <DialogContentText color="error" sx={{ mt: 1 }}>
                            {error}
                        </DialogContentText>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={loading}>
                        Отмена
                    </Button>
                    <Button
                        onClick={handleRemove}
                        color="error"
                        variant="contained"
                        disabled={loading}
                        disableElevation
                    >
                        {loading ? <CircularProgress size={18} /> : 'Удалить'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

interface CreatedTokenDialogProps {
    token: string | null;
    onClose: () => void;
}

function CreatedTokenDialog({ token, onClose }: CreatedTokenDialogProps) {
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        setCopied(false);
    }, [token]);

    if (!token) return null;

    const handleCopy = async () => {
        await navigator.clipboard.writeText(token);
        setCopied(true);
    };

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Токен создан</DialogTitle>
            <DialogContent>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Скопируйте токен сейчас. Повторно получить его будет нельзя.
                </Alert>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                        component="code"
                        sx={{ flex: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, overflowWrap: 'anywhere' }}
                    >
                        {token}
                    </Typography>
                    <Tooltip title={copied ? 'Скопировано' : 'Копировать'}>
                        <IconButton onClick={handleCopy} aria-label="Копировать токен">
                            <ContentCopyIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained" disableElevation>
                    Закрыть
                </Button>
            </DialogActions>
        </Dialog>
    );
}

interface TokenNodeItemProps {
    node: DbNode;
    depth: number;
}

const TokenNodeItem = observer(({ node, depth }: TokenNodeItemProps) => {
    const [store] = useState(() => new AdminNodeStore(node.node_id));
    const [tokens, setTokens] = useState<Token[]>([]);
    const [tokensLoading, setTokensLoading] = useState(true);
    const [error, setError] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createdToken, setCreatedToken] = useState<string | null>(null);

    const loadTokens = async () => {
        setTokensLoading(true);
        try {
            const answer = await TokenService.getTokens(node.node_id);
            setTokens(answer.data);
        } catch {
            setError(true);
        } finally {
            setTokensLoading(false);
        }
    };

    useEffect(() => {
        store.loadNode().catch(() => setError(true));
        loadTokens();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCreate = async () => {
        setCreating(true);
        setError(false);
        try {
            const answer = await TokenService.createToken(node.node_id);
            await loadTokens();
            setCreatedToken(answer.data.token);
        } catch {
            setError(true);
        } finally {
            setCreating(false);
        }
    };

    const tokenRows = tokens.map((token) => (
        <TokenRow key={token.id} token={token} depth={depth + 1} onRemoved={loadTokens} />
    ));
    const childNodes = store.items.map((child) => <TokenNodeItem key={child.node_id} node={child} depth={depth + 1} />);
    const content =
        tokens.length > 0 || store.items.length > 0 ? (
            <>
                {tokenRows}
                {childNodes}
            </>
        ) : undefined;

    return (
        <>
            <NodeView
                name={node.name}
                icon={node.node_id === 1 ? <LanguageIcon fontSize="small" /> : undefined}
                depth={depth}
                defaultOpen={depth === 0}
                actions={
                    node.node_id !== 1 && (
                        <Tooltip title="Создать токен">
                            <span>
                                <IconButton size="small" onClick={handleCreate} disabled={creating}>
                                    {creating ? <CircularProgress size={18} /> : <AddIcon fontSize="small" />}
                                </IconButton>
                            </span>
                        </Tooltip>
                    )
                }
            >
                {tokensLoading || store.loading ? <NodeChildrenSkeleton /> : content}
                {error && (
                    <Typography color="error" variant="body2" sx={{ px: 2, py: 1 }}>
                        Не удалось загрузить токены
                    </Typography>
                )}
            </NodeView>
            <CreatedTokenDialog token={createdToken} onClose={() => setCreatedToken(null)} />
        </>
    );
});

function TokenManager() {
    const { authStore } = useContext(AuthStoreContext);
    const [docsOpen, setDocsOpen] = useState(false);
    const user = authStore.user;

    if (!user) return null;

    const rootNode: DbNode = {
        node_id: user.node_id,
        parent_id: null,
        type: user.node_id === 1 ? NodeType.Root : NodeType.Organization,
        name: 'Всё',
        description: null,
    };

    return (
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
            <Box
                sx={{
                    px: 3,
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                }}
            >
                <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                        Управление токенами
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Токены доступа к данным по узлам организации.
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={<HelpOutlineIcon />}
                    onClick={() => setDocsOpen(true)}
                    sx={{ flexShrink: 0, textTransform: 'none' }}
                >
                    Документация API
                </Button>
            </Box>
            <Divider />
            <List dense disablePadding sx={{ p: 1 }}>
                <TokenNodeItem node={rootNode} depth={0} />
            </List>
            <TokenDocsDialog open={docsOpen} onClose={() => setDocsOpen(false)} />
        </Paper>
    );
}

export default TokenManager;
